import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { clientsService } from '@/services/clients-service'
import { PageLoader } from '@/components/ui/page-loader'
import { Pagination } from '@/components/ui/pagination'
import type { Client } from '@/types'
import { AddressAutocomplete } from '@/components/shared/address-autocomplete'

const PAGE_SIZE = 25

function UploadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
      <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
  )
}

// --- Add Customer Modal ---

function AddCustomerModal({
  onClose,
  onSave,
}: {
  onClose: () => void
  onSave: (client: Client) => void
}) {
  const [name,        setName]        = useState('')
  const [address,     setAddress]     = useState('')
  const [city,        setCity]        = useState('')
  const [stateVal,    setStateVal]    = useState('')
  const [zip,         setZip]         = useState('')
  const [isResidence, setIsResidence] = useState(false)
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState('')

  async function handleSave() {
    if (!name.trim()) {
      setError('Customer name is required.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const created = await clientsService.create({
        name:        name.trim(),
        address:     address.trim()  || undefined,
        city:        city.trim()     || undefined,
        state:       stateVal.trim() || undefined,
        zip:         zip.trim()      || undefined,
        isResidence,
      })
      onSave(created)
      onClose()
    } catch (err) {
      setError('Failed to create customer. Please try again.')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="cust-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cust-modal">
        <div className="cust-modal-header">
          <h2 className="cust-modal-title">Add Customer</h2>
          <button className="cust-modal-close" onClick={onClose}><XIcon /></button>
        </div>
        <div className="cust-modal-body">
          <div className="cust-modal-field">
            <label className="cust-modal-label">Customer / Company Name *</label>
            <input
              className="cust-modal-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Riverside Property Management"
              autoFocus
            />
          </div>
          <div className="cust-modal-field">
            <label className="cust-modal-label">Street Address</label>
            <AddressAutocomplete
              value={address}
              onChange={setAddress}
              onSelect={(street, c, s, z) => { setAddress(street); setCity(c); setStateVal(s); setZip(z) }}
              placeholder="Start typing an address..."
              className="cust-modal-input"
            />
          </div>
          <div className="cust-modal-row">
            <div className="cust-modal-field" style={{ flex: 2 }}>
              <label className="cust-modal-label">City</label>
              <input className="cust-modal-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
            </div>
            <div className="cust-modal-field" style={{ flex: 1 }}>
              <label className="cust-modal-label">State</label>
              <input className="cust-modal-input" value={stateVal} onChange={(e) => setStateVal(e.target.value)} placeholder="CA" />
            </div>
            <div className="cust-modal-field" style={{ flex: 1 }}>
              <label className="cust-modal-label">Zip</label>
              <input className="cust-modal-input" value={zip} onChange={(e) => setZip(e.target.value)} placeholder="90210" />
            </div>
          </div>
          <div className="cust-modal-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              id="add-cust-residence"
              checked={isResidence}
              onChange={(e) => setIsResidence(e.target.checked)}
              style={{ width: 14, height: 14, accentColor: 'var(--primary)', cursor: 'pointer' }}
            />
            <label htmlFor="add-cust-residence" className="cust-modal-label" style={{ marginBottom: 0, cursor: 'pointer' }}>
              Residential customer
            </label>
          </div>
          {error && <p style={{ fontSize: '13px', color: 'var(--error, #e53e3e)', marginTop: 4 }}>{error}</p>}
        </div>
        <div className="cust-modal-footer">
          <button className="cust-modal-btn-cancel" onClick={onClose}>Cancel</button>
          <button className="cust-modal-btn-save" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Add Customer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Main ---

export function CustomersListPage() {
  const navigate = useNavigate()
  const [clients, setClients] = useState<Client[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [page, setPage] = useState(1)

  useEffect(() => {
    clientsService
      .list()
      .then(setClients)
      .catch(() => setClients([]))
      .finally(() => setIsLoading(false))
  }, [])

  const filtered = clients.filter((c) => {
    if (!search) return true
    const q = search.toLowerCase()
    const primary = c.contacts?.[0]
    const primaryName = primary ? [primary.firstName, primary.lastName].filter(Boolean).join(' ').toLowerCase() : ''
    return (
      c.name.toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q) ||
      (c.company || '').toLowerCase().includes(q) ||
      primaryName.includes(q)
    )
  })

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE)
  const pagedFiltered = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  function handleCustomerAdded(client: Client) {
    setClients((prev) => [client, ...prev].sort((a, b) => a.name.localeCompare(b.name)))
  }

  return (
    <>
    <div>
      {/* Page Header */}
      <div className="est-list-head">
        <h1 className="est-list-title">Customers</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-outline" style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', color: 'var(--blue)', borderColor: 'var(--blue)' }} onClick={() => navigate('/customers/import')}>
            <UploadIcon /> Import Customers
          </button>
          <button className="btn" style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setShowAdd(true)}>
            <PlusIcon /> Add Customer
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '16px' }}>
        <div className="search-wrap">
          <input
            type="text"
            className="search-input"
            placeholder="Search customers..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          />
          <span className="search-icon">
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ marginTop: '15px' }}>
        {isLoading ? (
          <PageLoader />
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <svg style={{ width: '48px', height: '48px', margin: '0 auto 12px', color: 'var(--s300)' }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            {search ? (
              <>
                <p style={{ fontWeight: 500, color: 'var(--s500)' }}>No customers found</p>
                <p style={{ fontSize: '13px', color: 'var(--s400)', marginTop: '4px' }}>Try a different search term.</p>
              </>
            ) : (
              <>
                <p style={{ fontWeight: 500, color: 'var(--s500)' }}>No customers yet</p>
                <p style={{ fontSize: '13px', color: 'var(--s400)', marginTop: '4px' }}>Add your first customer or import a list to get started.</p>
                <button className="btn" style={{ marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setShowAdd(true)}>
                  <PlusIcon /> Add Customer
                </button>
              </>
            )}
          </div>
        ) : (
          <>
          <table className="tbl">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {pagedFiltered.map((c) => (
                <tr
                  key={c.id}
                  className="est-list-row"
                  onClick={() => navigate(`/customers/${c.id}`)}
                >
                  <td>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ fontWeight: 600, color: 'var(--s900)', fontSize: '14px' }}>{c.name}</span>
                        {c.isResidence && (
                          <span className="cust-residential-badge">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M3 9.5L12 3l9 6.5V21a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
                            </svg>
                            Residential
                          </span>
                        )}
                      </div>
                      {(() => {
                        const primary = c.contacts?.find((ct) => ct.isPrimary) ?? c.contacts?.[0]
                        if (!primary) return null
                        const name = [primary.firstName, primary.lastName].filter(Boolean).join(' ')
                        return (
                          <div style={{ fontSize: 12, color: 'var(--s400)', marginTop: 1 }}>
                            {name}{primary.title ? ` · ${primary.title}` : ''}
                          </div>
                        )
                      })()}
                    </div>
                  </td>
                  <td>{c.phone || '—'}</td>
                  <td>{c.email || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
          </>
        )}
      </div>
    </div>

    {showAdd && (
      <AddCustomerModal
        onClose={() => setShowAdd(false)}
        onSave={handleCustomerAdded}
      />
    )}
    </>
  )
}
